import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";

export const PERMISSION_KEYS = [
  // Finance
  "can_view_all_finances",
  "can_view_ministry_finances",
  "can_record_income",
  "can_record_expenses",
  "can_edit_transactions",
  "can_delete_transactions",
  "can_manage_budgets",
  "can_view_budgets",
  "can_manage_funds",
  "can_connect_bank",
  "can_approve_plaid_transactions",
  "can_generate_reports",
  "can_generate_giving_statements",
  // People
  "can_view_all_members",
  "can_view_ministry_members",
  "can_add_members",
  "can_edit_members",
  "can_delete_members",
  "can_export_members",
  // Events
  "can_manage_all_events",
  "can_manage_ministry_events",
  "can_take_attendance",
  // Ministries
  "can_manage_organizations",
  // Communications
  "can_post_bulletins",
  // Admin
  "can_manage_users",
  "can_manage_roles",
  "can_manage_ministries",
  "can_manage_church_settings",
] as const;

export type PermissionKey = (typeof PERMISSION_KEYS)[number];

export const PERMISSION_GROUPS = [
  {
    label: "Finance",
    permissions: [
      { key: "can_view_all_finances" as PermissionKey, label: "View all finances", description: "See all church financial data" },
      { key: "can_view_ministry_finances" as PermissionKey, label: "View ministry finances", description: "See finances for assigned ministries only" },
      { key: "can_record_income" as PermissionKey, label: "Record income", description: "Add tithes, offerings, and donations" },
      { key: "can_record_expenses" as PermissionKey, label: "Record expenses", description: "Add expense entries" },
      { key: "can_edit_transactions" as PermissionKey, label: "Edit transactions", description: "Modify existing entries" },
      { key: "can_delete_transactions" as PermissionKey, label: "Delete transactions", description: "Remove entries" },
      { key: "can_manage_budgets" as PermissionKey, label: "Manage budgets", description: "Create and edit budgets" },
      { key: "can_view_budgets" as PermissionKey, label: "View budgets", description: "View budget reports" },
      { key: "can_manage_funds" as PermissionKey, label: "Manage funds", description: "Create/edit designated funds" },
      { key: "can_connect_bank" as PermissionKey, label: "Connect bank", description: "Plaid bank integration" },
      { key: "can_approve_plaid_transactions" as PermissionKey, label: "Approve bank transactions", description: "Import bank transactions" },
      { key: "can_generate_reports" as PermissionKey, label: "Generate reports", description: "Create financial reports" },
      { key: "can_generate_giving_statements" as PermissionKey, label: "Giving statements", description: "Generate member giving statements" },
    ],
  },
  {
    label: "People",
    permissions: [
      { key: "can_view_all_members" as PermissionKey, label: "View all members", description: "See full member directory" },
      { key: "can_view_ministry_members" as PermissionKey, label: "View ministry members", description: "See members in assigned ministries only" },
      { key: "can_add_members" as PermissionKey, label: "Add members", description: "Create new member records" },
      { key: "can_edit_members" as PermissionKey, label: "Edit members", description: "Update member information" },
      { key: "can_delete_members" as PermissionKey, label: "Delete members", description: "Remove member records" },
      { key: "can_export_members" as PermissionKey, label: "Export members", description: "Download member data" },
    ],
  },
  {
    label: "Events",
    permissions: [
      { key: "can_manage_all_events" as PermissionKey, label: "Manage all events", description: "Create and edit all church events" },
      { key: "can_manage_ministry_events" as PermissionKey, label: "Manage ministry events", description: "Manage events for assigned ministries" },
      { key: "can_take_attendance" as PermissionKey, label: "Take attendance", description: "Record event attendance" },
    ],
  },
  {
    label: "Ministries",
    permissions: [
      { key: "can_manage_organizations" as PermissionKey, label: "Manage ministries", description: "Create and edit ministries" },
    ],
  },
  {
    label: "Communications",
    permissions: [
      { key: "can_post_bulletins" as PermissionKey, label: "Post bulletins", description: "Create and publish bulletins" },
    ],
  },
  {
    label: "Administration",
    permissions: [
      { key: "can_manage_users" as PermissionKey, label: "Manage users", description: "Invite and remove team members" },
      { key: "can_manage_roles" as PermissionKey, label: "Manage roles", description: "Create and edit roles" },
      { key: "can_manage_ministries" as PermissionKey, label: "Manage ministries", description: "Create and edit ministries" },
      { key: "can_manage_church_settings" as PermissionKey, label: "Church settings", description: "Edit church profile and settings" },
    ],
  },
];

// Legacy feature keys for backward compatibility with existing components
export const FEATURES = [
  { key: "members", label: "Members", description: "View and manage church members" },
  { key: "families", label: "Families", description: "View and manage family groups" },
  { key: "finance", label: "Finance", description: "View and manage tithes, offerings, and expenses" },
  { key: "contributions", label: "Contributions", description: "View contribution reports" },
  { key: "attendance", label: "Attendance", description: "Track event attendance" },
  { key: "events", label: "Events", description: "View and manage events" },
  { key: "communication", label: "Communication", description: "Send messages and announcements" },
  { key: "ministries", label: "Ministries", description: "View and manage ministries" },
  { key: "settings", label: "Settings", description: "Access church settings" },
  { key: "billing", label: "Billing", description: "Manage subscription and billing" },
] as const;

export type FeatureKey = (typeof FEATURES)[number]["key"];

export interface ChurchRole {
  id: string;
  church_id: string;
  name: string;
  description: string | null;
  system_level: string;
  [key: string]: any;
}

export interface RolePermission {
  role: string;
  feature: string;
  enabled: boolean;
}

export function usePermissions() {
  const { role, user, userRoleId, roleId, ministryScope } = useAuth();
  const { profile } = useChurch();
  const normalizedRole = (role || "").trim().toLowerCase().replace(/\s+/g, "_");

  // Fetch the church role details (granular permissions)
  const { data: churchRole = null, ...roleQuery } = useQuery({
    queryKey: ["church_role", roleId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("church_roles")
        .select("*")
        .eq("id", roleId!)
        .single();
      if (error) throw error;
      return data as ChurchRole;
    },
    enabled: !!roleId,
  });

  // Fetch ministry assignments
  const { data: ministryAssignments = [] } = useQuery({
    queryKey: ["user_ministry_assignments", userRoleId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("user_ministry_assignments")
        .select("organization_id, title")
        .eq("user_role_id", userRoleId!);
      if (error) throw error;
      return (data || []) as { organization_id: string; title: string | null }[];
    },
    enabled: !!userRoleId,
  });

  // Fetch legacy role_permissions for backward compat during transition
  const { data: legacyPermissions = [], ...legacyPermissionsQuery } = useQuery({
    queryKey: ["role_permissions", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("role_permissions")
        .select("role, feature, enabled")
        .eq("church_id", profile!.id);
      if (error) throw error;
      return data as RolePermission[];
    },
    enabled: !!profile?.id && !!user && !roleId, // Only fetch if no role_id (legacy mode)
  });

  const isChurchAdmin = normalizedRole === "admin" || normalizedRole === "church_admin" || churchRole?.system_level === "church_admin";

  // Granular permission check
  const can = (permission: PermissionKey): boolean => {
    if (isChurchAdmin) return true;
    if (!churchRole) {
      // Fallback: admin text role
      if (normalizedRole === "admin" || normalizedRole === "church_admin") return true;
      return false;
    }
    return churchRole[permission] === true;
  };

  // Ministry scoping
  const getRoleScopedMinistryIds = (): string[] => {
    const ids = (churchRole as any)?.scoped_ministry_ids;
    if (!Array.isArray(ids)) return [];
    return ids.filter((id): id is string => typeof id === "string" && id.length > 0);
  };

  const getEffectiveScopedMinistryIds = (): string[] => {
    if (isChurchAdmin) return [];

    // Role-level scoped_ministry_ids is the source of truth
    const roleScoped = getRoleScopedMinistryIds();
    if (roleScoped.length > 0) return roleScoped;

    // Fallback: if ministry_scope is explicitly "all" and no role-level ids, no restriction
    if (ministryScope === "all") return [];

    return ministryAssignments.map((a) => a.organization_id);
  };

  const canAccessMinistry = (organizationId: string): boolean => {
    if (isChurchAdmin) return true;
    const scoped = getEffectiveScopedMinistryIds();
    if (scoped.length === 0) return true;
    return scoped.includes(organizationId);
  };

  const ministryFilter = (): string[] | null => {
    if (isChurchAdmin) return null;
    const scoped = getEffectiveScopedMinistryIds();
    return scoped.length > 0 ? scoped : null;
  };

  // Legacy hasAccess for backward compatibility
  const hasAccess = (feature: FeatureKey): boolean => {
    if (isChurchAdmin) return true;

    // If we have granular permissions (church_role), use those
    if (churchRole) {
      switch (feature) {
        case "members":
        case "families":
          return can("can_view_all_members") || can("can_view_ministry_members") || can("can_add_members");
        case "finance":
        case "contributions":
          return can("can_view_all_finances") || can("can_view_ministry_finances") || can("can_record_income");
        case "attendance":
          return can("can_manage_all_events") || can("can_manage_ministry_events") || can("can_take_attendance");
        case "events":
          return can("can_manage_all_events") || can("can_manage_ministry_events") || can("can_take_attendance");
        case "communication":
          return can("can_post_bulletins");
        case "ministries":
          return can("can_manage_organizations") || can("can_manage_ministries");
        case "settings":
          return can("can_manage_church_settings") || can("can_manage_users") || can("can_manage_roles");
        case "billing":
          return false;
        default:
          return false;
      }
    }

    // Legacy mode: check role_permissions table
    if (!role) return false;
    const perm = legacyPermissions.find((p) => p.role === role && p.feature === feature);
    if (!perm) return false;
    return perm.enabled;
  };

  // Legacy finance organization scoping
  const FINANCE_SCOPE_PREFIX = "finance_org:";

  const getFinanceOrganizationIds = (): string[] => {
    if (isChurchAdmin) return [];

    // New system: role-level scoped ministries or user assignments fallback
    if (churchRole) {
      return getEffectiveScopedMinistryIds();
    }

    // Legacy system
    if (!role) return [];
    return legacyPermissions
      .filter((p) => p.role === role && p.enabled && p.feature.startsWith(FINANCE_SCOPE_PREFIX))
      .map((p) => p.feature.slice(FINANCE_SCOPE_PREFIX.length))
      .filter(Boolean);
  };

  const hasFinanceScopedAccess = (): boolean => {
    if (churchRole) {
      return !can("can_view_all_finances") && getFinanceOrganizationIds().length > 0;
    }
    return getFinanceOrganizationIds().length > 0;
  };

  const getPeopleOrganizationIds = (): string[] => {
    if (isChurchAdmin) return [];
    if (churchRole) return getEffectiveScopedMinistryIds();
    return [];
  };

  const hasPeopleScopedAccess = (): boolean => {
    if (churchRole) {
      return !can("can_view_all_members") && getPeopleOrganizationIds().length > 0;
    }
    return false;
  };

  const getEventsOrganizationIds = (): string[] => {
    if (isChurchAdmin) return [];
    if (churchRole) return getEffectiveScopedMinistryIds();
    return [];
  };

  const hasEventsScopedAccess = (): boolean => {
    if (churchRole) {
      return !can("can_manage_all_events") && getEventsOrganizationIds().length > 0;
    }
    return false;
  };

  const getAttendanceOrganizationIds = (): string[] => {
    if (isChurchAdmin) return [];
    if (churchRole) return getEffectiveScopedMinistryIds();
    return [];
  };

  const hasAttendanceScopedAccess = (): boolean => {
    if (churchRole) {
      return !can("can_manage_all_events") && getAttendanceOrganizationIds().length > 0;
    }
    return false;
  };

  const permissionsLoading = roleQuery.isLoading || legacyPermissionsQuery.isLoading;

  return {
    churchRole,
    can,
    canAccessMinistry,
    ministryFilter,
    hasAccess,
    isChurchAdmin,
    ministryScope: ministryScope || "all",
    assignedMinistries: ministryAssignments,
    permissions: legacyPermissions,
    getFinanceOrganizationIds,
    hasFinanceScopedAccess,
    getPeopleOrganizationIds,
    hasPeopleScopedAccess,
    getEventsOrganizationIds,
    hasEventsScopedAccess,
    getAttendanceOrganizationIds,
    hasAttendanceScopedAccess,
    permissionsLoading,
    ...roleQuery,
  };
}
